import { Body } from '../../typography';
import { createColumnHelper } from '@tanstack/react-table';

import TableColumnHeader from '../TableColumnHeader';

import type { CellContext } from '@tanstack/react-table';
import type { ColumnFactory, SortableColumnOptions } from './types';

/**
 * Factory function to create a sortable text column
 *
 * @example
 * ```tsx
 * const NAME_COLUMN = createSortableColumn<Customer>({
 *   accessor: 'fullName',
 *   label: 'Customer name',
 *   options: { sortable: true, size: 600 }
 * });
 * ```
 */
export function createSortableColumn<TData>(config: {
  accessor: keyof TData & string;
  label: string;
  options?: SortableColumnOptions<TData>;
}): ColumnFactory<TData> {
  const { accessor, label, options = {} } = config;
  const { size = 150, sortable = false, className, infoTooltip, formatter } = options;

  const columnHelper = createColumnHelper<TData>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return columnHelper.accessor(accessor as any, {
    size,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    header: ({ table }: { table: any }) => (
      <TableColumnHeader
        accessor={accessor}
        infoTooltip={infoTooltip}
        label={label}
        {...(sortable ? { setSorting: table.setSorting, sortable: true as const } : {})}
        tableState={table.getState()}
      />
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cell: (info: CellContext<TData, any>) => {
      const value = info.getValue();
      const displayValue = formatter ? formatter(value, info.row.original) : String(value);

      return <Body className={className}>{displayValue}</Body>;
    },
  });
}
